import {
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
  ViewChild,
} from '@angular/core';
import { environment } from 'src/environments';
import { CommonModule } from '@angular/common';
import { ThreedViewerMode } from './model';
import { TreatmentApiClient } from 'app/core/api-clients';
import { ArchType, TreatmentFileKind, TreatmentScanType, TreatmentType } from 'app/core/enums';
import { getFileTypeFromS3Link } from 'app/core/utils';
import { FileInfo } from 'app/core/models';
import { combineLatest, filter, first, firstValueFrom, fromEvent, map, ReplaySubject, shareReplay } from 'rxjs';
import { ColorConfig, ScanFileColor } from './constants';
import { FileTypes } from 'app/core/constants';
import { RxHelper } from 'app/core/services';
import { SafePipe } from 'safe-pipe';
import { v4 as uuid } from 'uuid';

@Component({
  selector: 'wl-threed-viewer',
  standalone: true,
  imports: [CommonModule, SafePipe],
  templateUrl: './threed-viewer.component.html',
  styleUrl: './threed-viewer.component.scss',
  providers: [RxHelper],
})
export class ThreedViewerComponent implements OnInit, OnChanges, OnDestroy {
  private readonly _treatmentClient = inject(TreatmentApiClient);
  private readonly _ngInitialized$ = new ReplaySubject<number>();

  @Input() files?: FileInfo[] | null;
  @Input() mode = ThreedViewerMode.auto;
  @Input() colorConfig?: ColorConfig | null;
  @Input() archType?: ArchType | null;
  @Input() treatmentType?: TreatmentType | null;

  @ViewChild('iframe', { static: true }) iframe?: ElementRef<HTMLIFrameElement> | null;

  protected readonly threedViewerHref = environment.sprintray3DViewerUrl;
  protected readonly threedViewerOrigin = `${new URL(environment.sprintray3DViewerUrl).origin}`;
  protected readonly receivedPageLoadedFromIframe$ = fromEvent<MessageEvent>(window, 'message').pipe(
    filter(e => e.origin === this.threedViewerOrigin && e.data.pageLoaded),
    map(e => ({ ...e, uuid: uuid() })),
    shareReplay(1)
  );

  private get parsedMode(): Exclude<`${ThreedViewerMode}`, 'auto'> {
    if (this.mode !== ThreedViewerMode.auto) return this.mode;

    return this.files && this.files.length > 1 ? ThreedViewerMode.grouping : ThreedViewerMode.single;
  }

  ngOnInit() {
    this._ngInitialized$.next(Date.now());
  }

  ngOnDestroy() {
    this._ngInitialized$.complete();
  }

  ngOnChanges(changes: Record<keyof ThreedViewerComponent, SimpleChange>) {
    if ((changes.files || changes.mode || changes.colorConfig) && this.colorConfig) {
      combineLatest([this._ngInitialized$, this.receivedPageLoadedFromIframe$])
        .pipe(first())
        .subscribe(() => {
          this.initialize().catch(console.warn);
        });
    }
  }

  private async initialize() {
    const { files, parsedMode, colorConfig, archType, treatmentType } = this;

    if (files && files.length > 0 && colorConfig != null) {
      const downloadLinks = await firstValueFrom(
        this._treatmentClient
          .getDownloadLinks(files.map(iFile => ({ ...iFile, fileId: iFile.id, fileExtension: iFile.extension })))
          .pipe(map(links => links.reduce<Record<string, string>>((acc, c) => ({ ...acc, [c.fileId]: c.url }), {})))
      );
      const models: unknown[] = [];

      if (parsedMode === ThreedViewerMode.single) {
        const file = files[0];
        const isScan = [TreatmentFileKind.Original, TreatmentFileKind.Revision].includes(file.fileKind);
        const fileUrl = downloadLinks[file.id];
        models.push({
          modelId: file.id,
          isScan,
          modelName: file.name,
          url: fileUrl,
          fileType: getFileTypeFromS3Link(fileUrl),
          meshColor: colorConfig.modelColor ?? ScanFileColor,
          modelType: file.fileType,
          isHidden: false,
        });
      } else if (parsedMode === ThreedViewerMode.grouping) {
        const allScans = files.filter(
          item =>
            (item.fileKind === TreatmentFileKind.Original || item.fileKind === TreatmentFileKind.ImprovedOriginal) &&
            (item.type === FileTypes.stl || item.type === FileTypes.drc)
        );
        const improvedScans = allScans.filter(item => item.fileKind === TreatmentFileKind.ImprovedOriginal);
        // Only include original scan whose file type not appeared in the improvedScans
        const scans = [
          ...allScans.filter(
            item =>
              item.fileKind === TreatmentFileKind.Original &&
              !improvedScans.some(improvedScan => improvedScan.fileType === item.fileType)
          ),
          ...improvedScans,
        ];

        const outputModels = files.filter(
          item =>
            item.fileKind === TreatmentFileKind.Design && (item.type === FileTypes.stl || item.type === FileTypes.drc)
        );
        const scanModelsData = scans.map(scan => {
          const iUrl = downloadLinks[scan.id];
          return {
            modelId: scan.id,
            isScan: true,
            modelName: scan.name,
            url: iUrl,
            fileType: getFileTypeFromS3Link(iUrl),
            meshColor: ScanFileColor,
            modelType: scan.fileType,
            isHidden: false,
          };
        });

        const outputModelsData = outputModels.map(outputModel => {
          const iUrl = downloadLinks[outputModel.id];
          const outputModelColorConfig = colorConfig.treatmentFiles?.find(
            item => item.fileType === outputModel.fileType
          );
          const outputModelColor = outputModelColorConfig ? outputModelColorConfig : colorConfig;
          return {
            modelId: outputModel.id,
            isScan: false,
            modelName: outputModel.name,
            url: iUrl,
            fileType: getFileTypeFromS3Link(iUrl),
            meshColor: outputModelColor.modelColor,
            crossColor: outputModelColor.crossColor,
            modelType: outputModel.fileType,
            isHidden: outputModel.fileType === TreatmentScanType.RemovableDieStl,
          };
        });

        models.push(...scanModelsData, ...outputModelsData);
      }

      const payload = {
        treatmentType,
        archType,
        isSingleModel: parsedMode === ThreedViewerMode.single,
        modelList: models,
        isDarkTheme: false,
        showFTUE: false,
      };

      console.info('threed viewer', payload);
      this.iframe?.nativeElement.contentWindow?.postMessage(payload, '*');
    }
  }
}
